import { useEffect, useState } from 'react';

import { countries } from 'constants/countries';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AnimatedModal } from 'components/AnimatedModal/AnimatedModal';
import {
  Button,
  FilePicker,
  InputGroup,
  Select,
  ToggleSwitchInput,
} from 'components/FormComponents';

export const DocumentUploadModal = ({
  visible,
  companyId,
  onClose = () => {},
  onUpload = async () => {},
  onDualUpload = async () => {},
  title = 'Upload document',
  description,
  selectedType,
  selectedCountry,
  onTypeChange = () => {},
  onCountryChange = () => {},
  options = [],
  metadata = {},
  idDetails = undefined,
  modelParams = {},
  bucketFolder,
  signUpload,
  deleteUpload,
  createUploadAction = false,
  onlyUpload = true,
  accept,
  icon = 'upload',
}) => {
  const { t } = useTranslation();

  const { handleSubmit, control, reset, register, watch, setValue } = useForm({
    defaultValues: {
      selectedType: selectedType,
      is_dual_upload: false,
    },
  });

  const [uploadData, setUploadData] = useState(null);
  const [complementaryUploadData, setComplementaryUploadData] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [isDualUpload, setIsDualUpload] = useState(false);

  useEffect(() => {
    if (!visible) {
      setUploadData(null);
      setComplementaryUploadData(null);
      setSubmitting(false);
      setIsDualUpload(false);
      reset();
    }
  }, [visible, reset]);

  const setupUploadData = data => {
    if (!data) {
      setUploadData(null);
      return;
    }
    setUploadData({
      ...data,
      type: selectedType,
      id_details: idDetails || undefined,
    });
  };

  const setupComplementaryUploadData = data => {
    if (!data) {
      setComplementaryUploadData(null);
      return;
    }
    setComplementaryUploadData({
      ...data,
      type: selectedType,
      id_details: idDetails || undefined,
    });
  };

  const hasUploadReady = !!uploadData?.hash_key;
  const hasDualUploadReady = !complementaryUploadData || !!complementaryUploadData?.hash_key;

  const buildUploadPayload = (data, dualSide) => {
    if (!data) return null;
    const payloadMetadata = dualSide != null ? { ...metadata, dual: dualSide } : { ...metadata };
    return {
      ...data,
      metadata: payloadMetadata,
      type: selectedType,
      id_details: idDetails || undefined,
    };
  };

  const onSubmit = async () => {
    if (!hasUploadReady || !hasDualUploadReady) {
      return;
    }

    setSubmitting(true);
    try {
      if (complementaryUploadData) {
        await onDualUpload(
          buildUploadPayload(uploadData, 'front'),
          buildUploadPayload(complementaryUploadData, 'back'),
        );
      } else {
        await onUpload(buildUploadPayload(uploadData, isDualUpload ? 'front' : null));
      }
      reset();
      onClose();
    } catch (error) {
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatedModal
      visible={visible}
      onClose={onClose}
      title={title}
      description={description}
      small
      icon={icon}>
      {visible && (
        <form onSubmit={handleSubmit(onSubmit)}>
          {!onlyUpload && (
            <>
              <InputGroup title={`${t('common.documentType')}`} className="u-margin-bottom">
                <Select
                  value={selectedType}
                  useSearch={true}
                  useDefault={false}
                  placeholder={t('common.selectPlaceholder')}
                  inputProps={{
                    onChange: e => onTypeChange(e.target.value),
                  }}>
                  {(options || []).map(({ value, label }) => (
                    <Select.Item key={value} value={value}>
                      {label}
                    </Select.Item>
                  ))}
                </Select>
              </InputGroup>
              <InputGroup title={`${t('common.country')}`} className="u-margin-bottom">
                <Select
                  value={selectedCountry}
                  useSearch={true}
                  useDefault={false}
                  placeholder={t('common.selectCountryPlaceholder')}
                  inputProps={{
                    onChange: e => onCountryChange?.(e.target.value),
                  }}>
                  {(countries || []).map(({ alpha2, name }) => (
                    <Select.Item key={alpha2} value={alpha2}>
                      {name}
                    </Select.Item>
                  ))}
                </Select>
              </InputGroup>
              <ToggleSwitchInput
                text="Is the document a dual sided upload?"
                noMaxWidth
                subtext="Enable this to upload a dual sided document. This will upload two documents, one for the front and one for the back of the document."
                onChange={e => setIsDualUpload(e.target.checked)}
                value={isDualUpload}
              />
            </>
          )}
          <InputGroup
            title={isDualUpload ? `${t('common.frontOfDocument')}` : `${t('common.document')}`}
            className="u-margin-top">
            <Controller
              control={control}
              name="upload_id"
              render={({ field: { value } }) => (
                <FilePicker
                  onChange={setupUploadData}
                  value={value}
                  metadata={isDualUpload ? { ...metadata, dual: 'front' } : metadata}
                  companyId={companyId}
                  modelParams={modelParams}
                  bucketFolder={bucketFolder}
                  signUpload={signUpload}
                  deleteUpload={deleteUpload}
                  createUploadAction={createUploadAction}
                  accept={accept}
                />
              )}
            />
          </InputGroup>

          {isDualUpload && (
            <InputGroup title={`${t('common.backOfDocument')}`} className="u-margin-top">
              <Controller
                control={control}
                name="complementary_upload_id"
                render={({ field: { value } }) => (
                  <FilePicker
                    companyId={companyId}
                    onChange={setupComplementaryUploadData}
                    value={value}
                    metadata={{ ...metadata, dual: 'back' }}
                    modelParams={modelParams}
                    bucketFolder={bucketFolder}
                    signUpload={signUpload}
                    deleteUpload={deleteUpload}
                    createUploadAction={createUploadAction}
                  />
                )}
              />
            </InputGroup>
          )}
          <Button
            submit
            type="primary"
            large
            className="u-width-100 u-margin-top--large"
            disabled={
              submitting ||
              !hasUploadReady ||
              !hasDualUploadReady ||
              (!onlyUpload && (!selectedType || !selectedCountry))
            }>
            {submitting ? t('common.uploading', 'Uploading...') : t('common.upload', 'Upload')}
          </Button>
        </form>
      )}
    </AnimatedModal>
  );
};
